import { formatInTimeZone } from "date-fns-tz";
import { useState } from "react";

import { useAsync } from "@smart/itops-hooks-dom";
import { Alert } from "@smart/itops-sb-design-system-dom";
import {
  MatterSubmission,
  useQuerySubmission,
} from "@smart/manage-gql-client-dom";

import { AutomaticRemindersOffBanner } from "./automatic-reminders-off-banner";
import { buildReminderNotice, ReminderNotice } from "./reminder-helpers";
import { ReminderScheduleValue } from "../../types";

export type ReminderManagementSectionProps = {
  selected: MatterSubmission;
  schedule: ReminderScheduleValue | null | undefined;
  isLoadingReminderData?: boolean;
};

const formatReminderDate = (date: string) =>
  formatInTimeZone(`${date}T00:00:00Z`, "UTC", "MMM d, yyyy");

const reminderNoticeContent = (notice: ReminderNotice | undefined) => {
  if (!notice) return undefined;

  switch (notice.kind) {
    case "already-stopped":
      return {
        palette: "default" as const,
        content: "Scheduled reminders already stopped",
      };
    case "not-scheduled":
      return {
        palette: "default" as const,
        content: "This form isn't on the reminder schedule.",
      };
    case "due-soon":
      return {
        palette: "warning" as const,
        content:
          notice.when === "today"
            ? "A scheduled reminder goes out today. Sending one now means the client gets 2 reminders close together."
            : `A scheduled reminder goes out on ${formatReminderDate(notice.nextDate)}. Sending one now means the client gets 2 reminders close together.`,
      };
    case "will-stop":
      return {
        palette: "warning" as const,
        content: `Sending now cancels the ${notice.remaining} remaining scheduled reminder${notice.remaining === 1 ? "" : "s"}.`,
      };
    case "schedule-unaffected":
      return {
        palette: "default" as const,
        content: `Manual reminders don't affect the schedule. The next one goes out ${formatReminderDate(notice.nextDate)}.`,
      };
    default:
      return undefined;
  }
};

export const ReminderManagementSection = ({
  selected,
  schedule,
  isLoadingReminderData,
}: ReminderManagementSectionProps) => {
  const querySubmission = useQuerySubmission();
  const [lazyNextAutomaticReminderDate, setLazyNextAutomaticReminderDate] =
    useState<string | null>();
  const [dismissedNoticeKind, setDismissedNoticeKind] = useState<string>();

  const missingNextAutomaticReminderDate =
    selected.rawSubmission?.nextAutomaticReminderDate === undefined;

  useAsync(
    async (info) => {
      if (!missingNextAutomaticReminderDate) {
        setLazyNextAutomaticReminderDate(undefined);
        return;
      }

      const result = await querySubmission({ uri: selected.uri });
      if (info.mounted)
        setLazyNextAutomaticReminderDate(result?.nextAutomaticReminderDate);
    },
    [selected.uri, missingNextAutomaticReminderDate],
  );

  const reminder = selected.rawSubmission?.values?.reminder;
  const nextAutomaticReminderDate = missingNextAutomaticReminderDate
    ? lazyNextAutomaticReminderDate
    : selected.rawSubmission?.nextAutomaticReminderDate;

  const isLoadingNextAutomaticReminderDate =
    missingNextAutomaticReminderDate &&
    lazyNextAutomaticReminderDate === undefined;

  const dataStillLoading =
    !!isLoadingReminderData || isLoadingNextAutomaticReminderDate;

  const reminderNotice = buildReminderNotice({
    reminder,
    schedule,
    nextAutomaticReminderDate,
    now: new Date().toISOString(),
  });

  const showNotice =
    !dataStillLoading &&
    (reminderNotice.kind === "already-stopped" || !!schedule?.enabled);

  const notice = reminderNoticeContent(reminderNotice);
  const noticeVisible =
    showNotice && !!notice && dismissedNoticeKind !== reminderNotice.kind;

  return (
    <>
      {!dataStillLoading && (
        <AutomaticRemindersOffBanner scheduleEnabled={schedule?.enabled} />
      )}
      {noticeVisible && (
        <Alert
          variant="info"
          palette={notice.palette}
          content={notice.content}
          onClose={() => setDismissedNoticeKind(reminderNotice.kind)}
        />
      )}
    </>
  );
};
